import { createMuiTheme } from "@material-ui/core/styles";
import createBreakpoints, { keys } from "@material-ui/core/styles/createBreakpoints";
import { CSSProperties } from "@material-ui/styles/withStyles/withStyles";
import shadows, { Shadows } from "@material-ui/core/styles/shadows";
import { ColorPartial } from "@material-ui/core/styles/createPalette";

const breakpoints = createBreakpoints({
    values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        desktop: 1528,
        xl: 1920,
    },
});
keys.push(keys.splice(keys.length - 1, 1, "desktop").pop()!);

export type ApplicationColorPalette = "primary" | "secondary" | "error" | "warning" | "info" | "success" | "default";
export type FullColorPalette = ApplicationColorPalette | "indigo";
export type AdditionalColorPaletteKey = 1 | 2;
export type ShadowsPaletteKey = 1 | 2 | 4 | 8 | 16 | 24;
export type ApplicationShadowBackground = "bg1" | "bg2";
export type ShadowBackgroundKey = 8 | 24;
export type ShadowsPalette = { [key in ShadowsPaletteKey]: string };

export const mobileBreakpoint = "xs";
export const tabletBreakpoint = "sm";
export const mediumBreakpoint = "md";
export const largeBreakpoint = "lg";
export const desktopBreakpoint = "desktop";
export const extraLargeBreakpoint = "xl";

export const portraitOrientation = "@media (orientation: portrait)";
export const andPortraitOrientation = "and (orientation: portrait)";
export const landscapeOrientation = "@media (orientation: landscape)";
export const andLandscapeOrientation = "and (orientation: landscape)";

export const elementWidthSize = {
    default: "360px",
    small: "168px",
    medium: "460px",
    big: "752px",
};

export const APPLICATION_COLOR_PALETTES: { [key in ApplicationColorPalette]: ApplicationColorPalette } = {
    primary: "primary",
    secondary: "secondary",
    error: "error",
    warning: "warning",
    info: "info",
    success: "success",
    default: "default",
};

export const applicationShadowPalettes: { [key in ApplicationColorPalette]: ShadowsPalette } = {
    primary: {
        "1": "0px 0px 1px rgba(27, 78, 163, 0.16), 0px 2px 2px rgba(41, 121, 255, 0.16)",
        "2": "0px 1px 2px rgba(27, 78, 163, 0.2), 0px 2px 4px rgba(41, 121, 255, 0.2)",
        "4": "0px 2px 4px rgba(27, 78, 163, 0.12), 0px 4px 8px rgba(41, 121, 255, 0.12)",
        "8": "0px 4px 8px rgba(27, 78, 163, 0.1), 0px 8px 16px rgba(41, 121, 255, 0.1)",
        "16": "0px 8px 16px rgba(27, 78, 163, 0.1), 0px 16px 32px rgba(41, 121, 255, 0.1)",
        "24": "0px 12px 24px rgba(27, 78, 163, 0.08), 0px 24px 48px rgba(41, 121, 255, 0.08)",
    },
    secondary: {
        "1": "0px 0px 1px rgba(29, 146, 255, 0.16), 0px 2px 2px rgba(143, 201, 255, 0.16)",
        "2": "px 1px 2px rgba(29, 146, 255, 0.2), 0px 2px 4px rgba(143, 201, 255, 0.2)",
        "4": "0px 2px 4px rgba(29, 146, 255, 0.12), 0px 4px 8px rgba(143, 201, 255, 0.12)",
        "8": "0px 4px 8px rgba(29, 146, 255, 0.1), 0px 8px 16px rgba(143, 201, 255, 0.1)",
        "16": "0px 8px 16px rgba(29, 146, 255, 0.1), 0px 16px 32px rgba(143, 201, 255, 0.1)",
        "24": "0px 12px 24px rgba(29, 146, 255, 0.08), 0px 24px 48px rgba(143, 201, 255, 0.08)",
    },
    error: {
        "1": "0px 0px 1px rgba(156, 43, 35, 0.16), 0px 2px 2px rgba(244, 67, 54, 0.16)",
        "2": "0px 1px 2px rgba(156, 43, 35, 0.2), 0px 2px 4px rgba(244, 67, 54, 0.2)",
        "4": "0px 2px 4px rgba(156, 43, 35, 0.12), 0px 4px 8px rgba(244, 67, 54, 0.12)",
        "8": "0px 4px 8px rgba(156, 43, 35, 0.1), 0px 8px 16px rgba(244, 67, 54, 0.1)",
        "16": "0px 8px 16px rgba(156, 43, 35, 0.1), 0px 16px 32px rgba(244, 67, 54, 0.1)",
        "24": "0px 12px 24px rgba(156, 43, 35, 0.08), 0px 24px 48px rgba(244, 67, 54, 0.08)",
    },
    warning: {
        "1": "0px 0px 1px rgba(160, 83, 0, 0.16), 0px 2px 2px rgba(251, 130, 0, 0.16)",
        "2": "0px 1px 2px rgba(160, 83, 0, 0.2), 0px 2px 4px rgba(251, 130, 0, 0.2)",
        "4": "0px 2px 4px rgba(160, 83, 0, 0.12), 0px 4px 8px rgba(251, 130, 0, 0.12)",
        "8": "0px 4px 8px rgba(160, 83, 0, 0.1), 0px 8px 16px rgba(251, 130, 0, 0.1)",
        "16": "0px 8px 16px rgba(160, 83, 0, 0.1), 0px 16px 32px rgba(251, 130, 0, 0.1)",
        "24": "0px 12px 24px rgba(160, 83, 0, 0.08), 0px 24px 48px rgba(251, 130, 0, 0.08)",
    },
    info: {
        "1": "0px 0px 1px rgba(2, 136, 209, 0.16), 0px 2px 2px rgba(3, 169, 244, 0.16)",
        "2": "0px 1px 2px rgba(2, 136, 209, 0.2), 0px 2px 4px rgba(3, 169, 244, 0.2)",
        "4": "0px 2px 4px rgba(2, 136, 209, 0.12), 0px 4px 8px rgba(3, 169, 244, 0.12)",
        "8": "0px 4px 8px rgba(2, 136, 209, 0.1), 0px 8px 16px rgba(3, 169, 244, 0.1)",
        "16": "0px 8px 16px rgba(2, 136, 209, 0.1), 0px 16px 32px rgba(3, 169, 244, 0.1)",
        "24": "0px 12px 24px rgba(2, 136, 209, 0.08), 0px 24px 48px rgba(3, 169, 244, 0.08)",
    },
    success: {
        "1": "0px 0px 1px rgba(43, 102, 46, 0.16), 0px 2px 2px rgba(67, 160, 71, 0.16)",
        "2": "0px 1px 2px rgba(43, 102, 46, 0.2), 0px 2px 4px rgba(67, 160, 71, 0.2)",
        "4": "0px 2px 4px rgba(43, 102, 46, 0.12), 0px 4px 8px rgba(67, 160, 71, 0.12)",
        "8": "0px 4px 8px rgba(43, 102, 46, 0.1), 0px 8px 16px rgba(67, 160, 71, 0.1)",
        "16": "0px 8px 16px rgba(43, 102, 46, 0.1), 0px 16px 32px rgba(67, 160, 71, 0.1)",
        "24": "0px 12px 24px rgba(43, 102, 46, 0.08), 0px 24px 48px rgba(67, 160, 71, 0.08)",
    },
    default: {
        "1": "0px 0px 1px rgba(58, 58, 68, 0.16), 0px 2px 2px rgba(90, 91, 106, 0.16)",
        "2": "0px 1px 2px rgba(58, 58, 68, 0.2), 0px 2px 4px rgba(90, 91, 106, 0.2)",
        "4": "0px 2px 4px rgba(58, 58, 68, 0.12), 0px 4px 8px rgba(90, 91, 106, 0.12)",
        "8": "0px 4px 8px rgba(58, 58, 68, 0.1), 0px 8px 16px rgba(90, 91, 106, 0.1)",
        "16": "0px 8px 16px rgba(58, 58, 68, 0.1), 0px 16px 32px rgba(90, 91, 106, 0.1)",
        "24": "0px 12px 24px rgba(58, 58, 68, 0.08), 0px 24px 48px rgba(90, 91, 106, 0.08)",
    },
};

type AdditionalShadow = {
    [key in ShadowBackgroundKey]: string;
};

export const appAdditionalShadow: {
    [key in ApplicationShadowBackground]: AdditionalShadow;
} = {
    bg1: {
        "8": "0px 4px 8px rgba(27, 78, 163, 0.04), 0px 8px 16px rgba(41, 121, 255, 0.04)",
        "24": "none",
    },
    bg2: {
        "8": "none",
        "24": "0px 12px 24px rgba(27, 78, 163, 0.04), 0px 24px 48px rgba(41, 121, 255, 0.04)",
    },
};

export const applicationPalettes: {
    [key in FullColorPalette]: ColorPartial;
} = {
    primary: {
        "50": "#D8E6FF",
        "100": "#C4DAFF",
        "200": "#9DC2FF",
        "300": "#76A9FF",
        "400": "#4F91FF",
        "500": "#2979FF",
        "600": "#2264D1",
        "700": "#1B4EA3",
        "800": "#133774",
        "900": "#0C2146",
    },
    secondary: {
        "50": "#ECF6FF",
        "100": "#DAEDFF",
        "200": "#C7E4FF",
        "300": "#B4DBFF",
        "400": "#A2D2FF",
        "500": "#8FC9FF",
        "600": "#56AEFF",
        "700": "#1D92FF",
        "800": "#0076E3",
        "900": "#0058AB",
    },
    error: {
        "50": "#FDDCDA",
        "100": "#FCCBC8",
        "200": "#FAA9A3",
        "300": "#F8877F",
        "400": "#F6655A",
        "500": "#F44336",
        "600": "#C8372D",
        "700": "#9C2B23",
        "800": "#6F1F19",
        "900": "#43130F",
    },
    warning: {
        "50": "#FEE7CD",
        "100": "#FDDCB9",
        "200": "#FDC68B",
        "300": "#FCAF5C",
        "400": "#FB982E",
        "500": "#FB8200",
        "600": "#CE6B00",
        "700": "#A05300",
        "800": "#733C00",
        "900": "#452400",
    },
    info: {
        "50": "#E1F5FE",
        "100": "#B3E5FC",
        "200": "#81D4FA",
        "300": "#4FC3F7",
        "400": "#29B6F6",
        "500": "#03A9F4",
        "600": "#039BE5",
        "700": "#0288D1",
        "800": "#0277BD",
        "900": "#01579B",
    },
    success: {
        "50": "#DCEDDD",
        "100": "#CBE5CC",
        "200": "#A9D3AB",
        "300": "#87C289",
        "400": "#65B168",
        "500": "#43A047",
        "600": "#37833B",
        "700": "#2B662E",
        "800": "#1F4921",
        "900": "#132C14",
    },
    default: {
        "50": "#E1E1E3",
        "100": "#D2D2D6",
        "200": "#B4B4BB",
        "300": "#9696A0",
        "400": "#787885",
        "500": "#5A5B6A",
        "600": "#4A4B57",
        "700": "#3A3A44",
        "800": "#292A31",
        "900": "#19191D",
    },
    indigo: {
        "50": "#E8EAF5",
        "100": "#C6CAE6",
        "200": "#A1A8D6",
        "300": "#7B86C6",
        "400": "#5F6BBA",
        "500": "#4350AF",
        "600": "#3C49A5",
        "700": "#333F99",
        "800": "#2B358E",
        "900": "#1C2379",
    },
};

export const primaryApplicationFontFamily = "Roboto";
export const montserratFontFamily = "Montserrat";

export const textMainColor = "#0C2146";
export const textSecondColor = "#41495E";
export const textWhiteColor = "#FFFFFF";
export const bgGrayColor = "#FCFDFF"; // TODO: Move to theme palette

type AdditionalColor = {
    [key in AdditionalColorPaletteKey]: string;
};

export const appAdditionalPalettes: {
    [key in ApplicationColorPalette]: AdditionalColor;
} = {
    primary: {
        "1": "#F5F8FF",
        "2": "#EBF2FF",
    },
    secondary: {
        "1": "#fff",
        "2": "#fff",
    },
    error: {
        "1": "#FEF2F1",
        "2": "#FEE8E7",
    },
    warning: {
        "1": "#FFF8EB",
        "2": "#FFEFD1",
    },
    info: {
        "1": "#fff",
        "2": "#fff",
    },
    success: {
        "1": "#F5FAF5",
        "2": "#ECF7ED",
    },
    default: {
        "1": "#F7F7FA",
        "2": "#EDEDF0",
    },
};

export enum Gradients {
    Gr1 = "linear-gradient(272.95deg, #F5F8FF 0%, #FAFBFF 100%)",
    Gr2 = "linear-gradient(272.95deg, #EBF2FF 0%, #F5F8FF 100%)",
}

export enum TxtColor {
    Txt1 = "#071B3F",
    Txt2 = "#384864",
    Txt3 = "#687388",
    Txt4 = "#818A9C",
    Txt5 = "#A5AEBE",
    Txt6 = "#BAC3D2",
    Txt7 = "#E6ECF5",
    Txt8 = "#FFFFFF",
}

const commonHeadingStyles: CSSProperties = {
    fontFamily: montserratFontFamily,
    color: TxtColor.Txt1,
};

const commonSubtitleStyles: CSSProperties = { fontFamily: montserratFontFamily, color: TxtColor.Txt1 };

const commonBodyStyles: CSSProperties = {
    fontFamily: primaryApplicationFontFamily,
    color: TxtColor.Txt1,
    fontWeight: 400,
};

const commonLargeHeadingStyles: CSSProperties = { ...commonHeadingStyles };
const commonSmallHeadingStyles: CSSProperties = {
    ...commonHeadingStyles,
    letterSpacing: "0.15px",
};

const captionStyles: CSSProperties = {
    fontSize: "0.75rem",
    letterSpacing: "0.4px",
    fontFamily: primaryApplicationFontFamily,
    fontWeight: 400,
    lineHeight: "1rem",
};

const customShadows: Shadows = [...shadows];
customShadows[1] = "0px 0px 1px rgba(58, 58, 68, 0.16), 0px 2px 2px rgba(90, 91, 106, 0.16)";
customShadows[2] = "0px 1px 2px rgba(58, 58, 68, 0.2), 0px 2px 4px rgba(90, 91, 106, 0.2)";
customShadows[4] = "0px 2px 4px rgba(58, 58, 68, 0.12), 0px 4px 8px rgba(90, 91, 106, 0.12)";
customShadows[8] = "0px 4px 8px rgba(58, 58, 68, 0.1), 0px 8px 16px rgba(90, 91, 106, 0.1)";
customShadows[16] = "0px 4px 8px rgba(58, 58, 68, 0.1), 0px 8px 16px rgba(90, 91, 106, 0.1)";
customShadows[24] = "0px 12px 24px rgba(58, 58, 68, 0.08), 0px 24px 48px rgba(90, 91, 106, 0.08)";

const theme = createMuiTheme({
    breakpoints,
    shadows: customShadows,
    zIndex: {
        appBar: 10,
        drawer: 10,
        tabHeader: 100,
        gallery: 110,
        chat: 2147483004,
        snackbar: 110,
        toast: 2147483006,
        modal: 2147483005,
        tooltip: 2147483007,
    },
    palette: {
        grey: applicationPalettes.default,
        primary: applicationPalettes.primary,
        success: applicationPalettes.success,
        warning: applicationPalettes.warning,
        error: applicationPalettes.error,
    },
    typography: {
        fontFamily: [
            primaryApplicationFontFamily,
            montserratFontFamily,
            "-apple-system",
            "BlinkMacSystemFont",
            '"Segoe UI"',
            "Roboto",
            '"Helvetica Neue"',
            "Arial",
            "sans-serif",
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
        ].join(","),
        button: {
            textTransform: "none",
        },
        fontSize: 16,
        fontWeightBold: 700,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        h1: {
            ...commonLargeHeadingStyles,
            fontSize: "2.5rem",
            lineHeight: "3.5rem",
            fontWeight: 700,
            letterSpacing: "0px",
            [breakpoints.down(mobileBreakpoint)]: {
                fontSize: "2.125rem",
                lineHeight: "3.125rem",
            },
        },
        h2: {
            ...commonLargeHeadingStyles,
            fontSize: "2rem",
            lineHeight: "3rem",
            fontWeight: 600,
            letterSpacing: "0.25px",
        },
        h3: {
            ...commonLargeHeadingStyles,
            fontSize: "1.5rem",
            lineHeight: "2.5rem",
            fontWeight: 600,
            letterSpacing: "0px",
        },
        h4: {
            ...commonSmallHeadingStyles,
            fontSize: "1.25rem",
            lineHeight: "2rem",
            fontWeight: 700,
        },
        h5: {
            ...commonSmallHeadingStyles,
            fontSize: "1.125rem",
            lineHeight: "2rem",
            fontWeight: 600,
        },
        h6: {
            ...commonSmallHeadingStyles,
            fontSize: "1rem",
            lineHeight: "2rem",
            fontWeight: 700,
        },
        caption: {
            ...captionStyles,
            color: TxtColor.Txt1,
        },
        subtitle1: {
            ...commonSubtitleStyles,
            fontSize: "1rem",
            lineHeight: "1.5rem",
            fontWeight: 400,
            letterSpacing: "0.15px",
        },
        subtitle2: {
            ...commonSubtitleStyles,
            fontSize: "0.875rem",
            lineHeight: "1.5rem",
            fontWeight: 500,
            letterSpacing: "0.1px",
        },
        overline: {
            color: TxtColor.Txt1,
            fontFamily: primaryApplicationFontFamily,
            fontSize: "0.625rem",
            lineHeight: "1rem",
            fontWeight: 400,
            letterSpacing: "1.5px",
        },
        body1: { ...commonBodyStyles, fontSize: "1rem", lineHeight: "2rem", letterSpacing: "0.3px" },
        body2: { ...commonBodyStyles, fontSize: "0.875rem", lineHeight: "1.5rem", letterSpacing: "0.25px" },
    },
    props: {
        MuiUseMediaQuery: { noSsr: true },
    },
    overrides: {
        MuiSvgIcon: {
            fontSizeSmall: {
                fontSize: "20px",
            },
        },
        MuiLink: {
            root: { fontFamily: primaryApplicationFontFamily, fontSize: "1rem", lineHeight: "2rem" },
        },
        MuiTypography: {
            root: {
                wordBreak: "break-word",
            },
            paragraph: {
                fontFamily: primaryApplicationFontFamily,
                fontSize: "1rem",
                lineHeight: "2rem",
                color: textSecondColor,
            },
        },
        MuiTooltip: {
            tooltip: {
                backgroundColor: applicationPalettes.primary["900"],
                ...captionStyles,
                color: textWhiteColor,
            },
        },
        MuiCssBaseline: {
            "@global": {
                body: {
                    whiteSpace: "pre-line",
                },
            },
        },
        MuiPickersBasePicker: {
            container: {
                width: "328px",
            },
        },
        MuiPickersYearSelection: {
            container: {
                padding: "24px",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                "&::-webkit-scrollbar": {
                    width: 0,
                },
                scrollbarWidth: "thin",
            },
        },
        MuiPickersYear: {
            root: {
                fontSize: `14px !important`,
                letterSpacing: `0.25px !important`,
                fontFamily: `${primaryApplicationFontFamily} !important`,
                margin: `10px 8px !important`,
                width: "72px",
                height: "36px",
                borderRadius: "30px",
            },
            yearSelected: {
                fontSize: `14px !important`,
                backgroundColor: applicationPalettes.primary["500"],
                color: `${TxtColor.Txt8} !important`,
                fontWeight: `400 !important`,
                margin: `10px 8px !important`,
            },
        },
        MuiPickersToolbarText: {
            toolbarTxt: {
                color: `${textWhiteColor} !important`,
            },
        },
        MuiPickersCalendarHeader: {
            dayLabel: {
                [breakpoints.down(tabletBreakpoint)]: {
                    margin: `0 2px !important`,
                },
            },
        },
        MuiTableRow: {
            root: {
                "&$hover:hover": {
                    backgroundColor: appAdditionalPalettes.primary[1],
                    cursor: "default !important",
                },
            },
            hover: {},
        },
    },
});

// Probably will be necessary when fixing PWA issues.
// theme = responsiveFontSizes(theme);

export default theme;
